/**
 * See https://openlayers.org/en/latest/examples/vector-esri.html
 * Ultimately this will only query for features it does not already have
 * It will make use the map SRS and the resulttype="tile" and exceededTransferLimit
 * See https://github.com/ca0v/ol3-lab/issues/4
 */

import ol = require("openlayers");
import { olx } from "openlayers";

import { defaults } from "ol3-fun/index";
import { StyleConverter } from "../format/ags-symbolizer";
import { LayerDefinition } from "ags-webmap";

const esrijsonFormat = new ol.format.EsriJSON();

function asParam(options: any) {
  return Object.keys(options)
    .map((k) => `${k}=${options[k]}`)
    .join("&");
}

function asSrs(wkid: number | string) {
  if (typeof wkid === "string") {
    const i = wkid.indexOf("EPSG:");
    if (0 === i) wkid = parseInt(wkid.substring(5));
    else if (!isNaN(parseInt(wkid))) {
      wkid = parseInt(wkid);
    }
  }
  return <number>wkid;
}

async function getLayerInfo(serviceUrl: string) {
  // f=json is returning a binary format (pbf most likely)
  const response = await fetch(`${serviceUrl}?f=json`);
  if (!response.ok) throw response.status;
  const layerInfo = await response.json();
  if (layerInfo.error) throw `${layerInfo.error.code}:${layerInfo.error.message}`;
  return layerInfo as LayerDefinition;
}

const incompleteResultStyle = new ol.style.Style({
  fill: new ol.style.Fill({
    color: "rgba(255, 0, 0, 0.1)",
  }),
  stroke: new ol.style.Stroke({
    width: 0.5,
    color: "rgba(255, 0, 0, 0.1)",
  }),
});

const loadingFeatureStyle = new ol.style.Style({
  fill: new ol.style.Fill({
    color: "rgba(255, 255, 255, 0.3)",
  }),
  stroke: new ol.style.Stroke({
    width: 1,
    color: "rgba(0, 0, 0, 1)",
  }),
});

export interface IOptions extends olx.source.VectorOptions {
  services: string;
  serviceName: string;
  serviceType: "FeatureServer" | "MapServer";
  map: ol.Map;
  layers: number[];
  tileSize?: number;
  where?: string;
  uidFieldName?: string;
  outFields?: string;
  maxQueryCount?: number;
}

const DEFAULT_OPTIONS = <IOptions>{
  tileSize: 512,
  where: "1=1",
  maxQueryCount: 1000,
  outFields: "*",
};

function makeFeature(extent: Array<number>) {
  const polygon = ol.geom.Polygon.fromExtent(extent as [number, number, number, number]);
  const feature = new ol.Feature({
    geometry: polygon,
  });
  return feature;
}

async function createLayer(options: IOptions & { layerId: number }) {
  const extentsToClear = [] as Array<ol.Extent>;
  const extentsToIgnore = [] as Array<ol.Extent>;
  const featuresToClear = [] as Array<ol.Feature>;
  let clearZoomLevel = 0;

  const { map } = options;
  const serviceUrl = `${options.services}/${options.serviceName}/${options.serviceType}/${options.layerId}`;
  const layerInfo = await getLayerInfo(serviceUrl);
  const { maxRecordCount, drawingInfo } = layerInfo;

  map.on("moveend", () => {
    if (clearZoomLevel >= map.getView().getZoom()) return;
    while (extentsToClear.length) vectorSource.removeLoadedExtent(<any>extentsToClear.pop());
    while (featuresToClear.length) vectorSource.removeFeature(<any>featuresToClear.pop());
  });

  const vectorSource = new ol.source.Vector({
    loader: async (extent, resolution, projection, success, failure) => {
      const extentFailedToQuery = extentsToIgnore.find((x) => ol.extent.equals(x, extent));
      if (!!extentFailedToQuery) {
        success && success([]);
        return;
      }
      const loadingFeature = makeFeature(extent);
      loadingFeature.setStyle(loadingFeatureStyle);
      vectorSource.addFeatures([loadingFeature]);

      const wkid = asSrs(map.getView().getProjection().getCode());

      const extentQuery = {
        xmin: extent[0],
        ymin: extent[1],
        xmax: extent[2],
        ymax: extent[3],
        spatialReference: { wkid: wkid },
      };

      const params = {
        f: "json",
        returnGeometry: true,
        spatialRel: "esriSpatialRelIntersects",
        geometry: encodeURIComponent(JSON.stringify(extentQuery)),
        geometryType: "esriGeometryEnvelope",
        resultType: "tile",
        where: options.where ? encodeURIComponent(options.where) : "",
        inSR: wkid,
        outSR: wkid,
        outFields: options.outFields || "*",
      };

      const paramsStr = asParam(params);
      const url = `${serviceUrl}/query?${paramsStr}`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw `${response.status}:${response.statusText}`;
        }
        const data = await response.json();

        if (data.error) {
          throw JSON.stringify(data.error);
        }

        vectorSource.removeFeature(loadingFeature);
        // dataProjection will be read from document
        const features = esrijsonFormat.readFeatures(data, {
          featureProjection: projection,
        });

        {
          // HPS-110401: this hack should make attributes available
          features.forEach((f) => f.set("attributes", f.getProperties()));
        }

        if (features.length >= maxRecordCount && 0 == features.length % maxRecordCount) {
          clearZoomLevel = map.getView().getZoom();
          extentsToClear.push(extent);
          extentsToIgnore.push(extent);
          let feature = makeFeature(extent);
          feature.setStyle(incompleteResultStyle);
          featuresToClear.push(feature);
          vectorSource.addFeatures([feature]);
        }
        vectorSource.addFeatures(features);
        success && success(features);
      } catch (ex) {
        extentsToClear.push(extent);
        console.error(ex);
        extentsToIgnore.push(extent);
        vectorSource.removeFeature(loadingFeature);
        failure && failure();
      }
    },
    strategy: ol.loadingstrategy.tile(
      ol.tilegrid.createXYZ({
        tileSize: 256,
      })
    ),
  });

  const vector = new ol.layer.Vector({
    source: vectorSource,
  });

  const converter = new StyleConverter();
  const styleMap = converter.fromRenderer(drawingInfo.renderer, { url: "for icons?" });

  vector.setStyle((feature: any) => {
    let style: ol.style.Style | null = null;
    if (styleMap instanceof ol.style.Style) {
      style = styleMap;
    } else {
      style = styleMap(feature);
    }
    return style;
  });

  return vector;
}

export class ArcGisVectorSourceFactory {
  static async create(options: IOptions) {
    options = defaults(options, DEFAULT_OPTIONS);

    const layers = await Promise.all(
      options.layers.map((layerId) => {
        const layer = createLayer({ layerId, ...options });
        return layer;
      })
    );
    return layers;
  }
}
